import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  MessageSquare,
  CheckCircle2,
  Sparkles,
  Zap,
  ShieldCheck,
  Info,
  ArrowRight,
} from "lucide-react";

/**
 * PUBLIC SMS signup / opt-in preview page.
 *
 * Purpose:
 *   1. Give Twilio A2P 10DLC campaign reviewers a URL they can visit WITHOUT
 *      logging in to verify the opt-in consent flow, disclosure language,
 *      Privacy/Terms links, and — critically — that SMS is entirely OPTIONAL
 *      and NOT a precondition for using SyncBook.
 *   2. Serve as a marketing-style landing page for the SMS feature.
 *
 * Design (post-30909-B fix, 2026-02-29):
 *   - No collapse toggle — the SMS form is visible inline so reviewers can
 *     inspect consent language WITHOUT any interaction. Twilio's reviewer
 *     interpreted a hidden form + required fields as "forced opt-in"; showing
 *     the form openly with heavy "OPTIONAL" framing addresses the actual concern.
 *   - Zero HTML `required` attributes; button `disabled` only enforces
 *     TCPA consent for users who CHOOSE to submit the opt-in.
 *   - Primary path (green "Continue without SMS") appears BEFORE the opt-in
 *     form and is visually dominant.
 *   - Field labels are explicit: "Required only if you're opting in".
 */
export default function SmsSignup() {
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false); // TCPA — MUST default false

  // Primary path — creates account with NO phone data at all.
  const skipSmsAndCreate = () => {
    try {
      window.sessionStorage.removeItem("syncbook_pending_sms_phone");
    } catch (err) {
      console.debug("sessionStorage clear failed:", err?.message);
    }
    nav("/register-account");
  };

  // Optional path — user has EXPLICITLY filled phone AND checked TCPA consent.
  const continueWithSms = (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error("Enter your phone number, or use the ‘Continue without SMS’ button above.");
      return;
    }
    if (!consent) {
      toast.error("Please check the TCPA consent box, or use the ‘Continue without SMS’ button above.");
      return;
    }
    try {
      window.sessionStorage.setItem("syncbook_pending_sms_phone", phone.trim());
    } catch (err) {
      console.debug("sessionStorage set failed:", err?.message);
    }
    nav("/register-account");
  };

  return (
    <div
      className="max-w-3xl mx-auto p-5 space-y-6 pb-16"
      data-testid="sms-signup-page"
    >
      <div className="flex items-center justify-between">
        <Link
          to="/"
          data-testid="sms-signup-back"
          className="nb-btn nb-btn-secondary !py-2 !px-3"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Home
        </Link>
        <div className="text-xs font-bold text-[#4a4a4a] uppercase tracking-wider">
          Optional feature
        </div>
        <div className="w-[70px]" />
      </div>

      {/* Hero */}
      <div className="nb-card-lg p-6 text-center space-y-3" style={{ background: "#d9d4f1" }}>
        <div className="inline-flex items-center gap-2 nb-tag !text-xs">
          <Sparkles size={12} strokeWidth={3} /> Optional SyncBook feature
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl">
          Log money without opening the app.
        </h1>
        <p className="text-sm font-medium max-w-xl mx-auto text-[#1a1a1a]">
          Text a quick shorthand to <span className="font-black">+1 417 962 8253</span>{" "}
          and SyncBook logs the transaction, categorises it, and replies with your
          updated balance — usually in under a second.
        </p>
      </div>

      {/* CRITICAL: explicit disclosure that SMS is NOT required */}
      <div
        className="nb-card p-4 flex items-start gap-2 text-[13px] font-medium"
        style={{ background: "#e5f0ff" }}
        data-testid="sms-optional-notice"
      >
        <Info size={16} strokeWidth={3} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-display font-black text-sm mb-0.5">
            SMS is 100% optional. No phone number is required to use SyncBook.
          </div>
          <div className="text-[12px] leading-snug">
            You can create a SyncBook account and use every feature —
            transactions, budgets, reconcile, forecasting, imports — without
            providing a phone number or agreeing to receive text messages.
            SMS is a convenience add-on you can enable now, later from your
            Profile page, or never.
          </div>
        </div>
      </div>

      {/* PRIMARY CTA — Continue without SMS.  Visually dominant, positioned
          BEFORE the opt-in form so the "no SMS" path is the obvious default. */}
      <div
        className="nb-card-lg p-6 space-y-3 text-center"
        style={{ background: "var(--positive)" }}
        data-testid="sms-signup-primary-cta-card"
      >
        <div className="font-display font-black text-xl">
          Create your SyncBook account
        </div>
        <p className="text-sm font-medium text-[#1a1a1a]">
          No phone number required. Skip SMS entirely and use all SyncBook
          features today. You can enable SMS later (or never) from Profile.
        </p>
        <button
          type="button"
          onClick={skipSmsAndCreate}
          data-testid="skip-sms-create-account"
          className="nb-btn nb-btn-dark w-full !py-3"
        >
          Continue without SMS →
        </button>
        <p className="text-[11px] font-medium text-[#4a4a4a]">
          No phone number will be collected on the next screen.
        </p>
      </div>

      {/* How it works — shown so reviewers understand the SMS feature */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="nb-card p-4 space-y-1.5" data-testid="how-1">
          <div className="flex items-center gap-1.5 font-display font-black text-base">
            <Zap size={14} strokeWidth={3} /> Send
          </div>
          <div className="text-xs font-medium text-[#4a4a4a]">
            Text <span className="font-mono bg-[#f4f0eb] px-1 rounded">-4.50 coffee</span>{" "}
            or <span className="font-mono bg-[#f4f0eb] px-1 rounded">+100 paycheck</span>.
          </div>
        </div>
        <div className="nb-card p-4 space-y-1.5" data-testid="how-2">
          <div className="flex items-center gap-1.5 font-display font-black text-base">
            <MessageSquare size={14} strokeWidth={3} /> Log
          </div>
          <div className="text-xs font-medium text-[#4a4a4a]">
            SyncBook parses, categorises, and adds it to your register — cleared.
          </div>
        </div>
        <div className="nb-card p-4 space-y-1.5" data-testid="how-3">
          <div className="flex items-center gap-1.5 font-display font-black text-base">
            <CheckCircle2 size={14} strokeWidth={3} /> Confirm
          </div>
          <div className="text-xs font-medium text-[#4a4a4a]">
            You get an ASCII reply — <span className="font-mono">OK: -4.50 coffee | Bal 233.50</span>.
          </div>
        </div>
      </div>

      {/* SKIP-THIS-SECTION banner directly above the opt-in form */}
      <div
        className="nb-card p-4 flex items-start gap-3 text-[13px] font-medium border-2 border-dashed"
        style={{ background: "#fff7d6", borderColor: "#e0b400" }}
        data-testid="sms-form-skip-banner"
      >
        <div className="text-2xl leading-none shrink-0">↓</div>
        <div className="flex-1">
          <div className="font-display font-black text-sm mb-0.5">
            The section below is OPTIONAL.
          </div>
          <div className="text-[12px] leading-snug">
            You can safely skip everything below this line. It is shown so our
            SMS Program consent language is publicly inspectable. Only fill it
            out if you affirmatively want to enable SMS — and even then, use
            the green button above to skip.
          </div>
          <button
            type="button"
            onClick={skipSmsAndCreate}
            data-testid="sms-form-skip-inline"
            className="mt-2 nb-btn nb-btn-secondary !py-1.5 !px-3 text-xs"
          >
            Skip — continue without SMS <ArrowRight size={12} strokeWidth={3} />
          </button>
        </div>
      </div>

      {/* Optional opt-in form — visible inline, framed as optional throughout */}
      <form
        onSubmit={continueWithSms}
        className="nb-card p-5 space-y-4"
        data-testid="sms-signup-form"
        noValidate
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} strokeWidth={3} />
          <div className="font-display font-black text-base">
            (Optional) Enable SMS text-in transactions
          </div>
        </div>

        <p className="text-[12px] font-medium text-[#4a4a4a]">
          This entire form is optional. You do NOT need to complete it to use
          SyncBook. Only fill it out if you WANT to enable SMS text-in on your
          new account. You can also enable SMS later, from Profile → Text-in
          transactions, without ever using this page.
        </p>

        <div>
          <label className="label" htmlFor="sms-signup-phone">
            Your mobile phone number{" "}
            <span className="text-[10px] font-medium text-[#4a4a4a] normal-case">
              (only required if you&apos;re opting in on this form)
            </span>
          </label>
          <input
            id="sms-signup-phone"
            data-testid="sms-signup-phone-input"
            type="tel"
            className="nb-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567  —  leave blank to skip"
            autoComplete="tel"
          />
          <div className="text-[11px] font-medium text-[#4a4a4a] mt-1">
            US format works: <span className="font-mono">555-123-4567</span> or with country
            code <span className="font-mono">+15551234567</span>. Leave blank if you don&apos;t
            want SMS.
          </div>
        </div>

        <label
          className="flex items-start gap-2 cursor-pointer select-none p-3 border-2 border-[#1a1a1a] rounded-xl bg-white"
          data-testid="sms-signup-consent-wrap"
        >
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="sms-signup-consent-checkbox"
            className="w-4 h-4 mt-0.5 shrink-0"
          />
          <span className="text-[11px] font-medium leading-snug">
            <span className="block text-[10px] uppercase tracking-wider text-[#4a4a4a] mb-0.5">
              (Only required if you filled in a phone number above)
            </span>
            I agree to receive transactional SMS from SyncBook at the number
            above under the{" "}
            <Link
              to="/sms-terms"
              data-testid="sms-signup-sms-terms"
              className="underline font-bold"
            >
              SMS Program Terms
            </Link>
            . Message frequency varies (typically 1–5 msgs/day). Message
            and data rates may apply. Reply <b>STOP</b> to unsubscribe or{" "}
            <b>HELP</b> for help at any time. My phone number will not be
            shared with third parties, affiliates, or marketers for any
            purpose. See our{" "}
            <Link
              to="/privacy"
              data-testid="sms-signup-privacy"
              className="underline font-bold"
            >
              Privacy Policy
            </Link>{" "}
            and general{" "}
            <Link
              to="/terms"
              data-testid="sms-signup-terms"
              className="underline font-bold"
            >
              Terms
            </Link>
            .
          </span>
        </label>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={skipSmsAndCreate}
            data-testid="sms-signup-skip-alt"
            className="nb-btn nb-btn-dark flex-1 !py-3"
          >
            Skip — continue without SMS
          </button>
          <button
            type="submit"
            data-testid="sms-signup-submit"
            disabled={!phone.trim() || !consent}
            className="nb-btn nb-btn-secondary flex-1 !py-3"
          >
            Enable SMS → next
          </button>
        </div>

        <p className="text-[11px] font-medium text-[#4a4a4a] text-center">
          Enabling SMS requires BOTH a phone number and TCPA consent per
          U.S. law. Leave the form blank to skip SMS entirely.
        </p>
      </form>

      <p
        className="text-[12px] font-medium text-[#4a4a4a] text-center"
        data-testid="sms-signup-review-link-wrap"
      >
        Twilio reviewers →{" "}
        <Link
          to="/sms-review"
          data-testid="sms-signup-review-link"
          className="underline font-bold"
        >
          A2P 10DLC compliance summary
        </Link>
      </p>

      <p className="text-[12px] font-medium text-[#4a4a4a] text-center">
        Already have an account?{" "}
        <Link
          to="/login"
          data-testid="sms-signup-login-link"
          className="underline font-bold"
        >
          Log in
        </Link>{" "}
        — you can also link SMS anytime from Profile → Text-in transactions.
      </p>

      {/* Reviewer-facing extras */}
      <div className="nb-card p-4 space-y-2 text-xs font-medium text-[#4a4a4a]" data-testid="sms-signup-details">
        <div className="font-display font-black text-sm text-[#1a1a1a]">
          What SyncBook will and will not do with your number
        </div>
        <ul className="list-disc pl-4 space-y-0.5">
          <li>
            <b>SMS is optional.</b> Every SyncBook feature is available without it.
          </li>
          <li>
            <b>Will:</b> reply to your inbound texts with a transaction confirmation and
            balance summary; store your number to match future inbound texts to your
            account (only if you opt in).
          </li>
          <li>
            <b>Will not:</b> send marketing, promotional, or third-party content over
            SMS; sell or share your phone number with any third party; text you unless
            you initiate.
          </li>
          <li>
            Unsubscribe at any time by texting <b>STOP</b> to the SyncBook number, or by
            unlinking your phone in the app under Profile → Text-in transactions.
          </li>
          <li>
            Full data-handling details are in our{" "}
            <Link to="/privacy" className="underline font-bold">
              Privacy Policy
            </Link>
            .
          </li>
        </ul>
      </div>
    </div>
  );
}
